'use strict';

const addressModel = require('./addressModel');
const GOOGLE_PLACES_AUTOCOMPLETE = require('../../util/constants').GOOGLE_PLACES_AUTOCOMPLETE;
var debounce = require('lodash/debounce');

/**
 * Removes the autocomplete dropdown from DOM
 * @param {HTMLElement} input - The input element
 */
function removeDropdown(input) {
    var dropdown = input.parentNode.querySelector('.pac-custom-container');
    if (dropdown) {
        dropdown.remove();
    }
}

/**
 * Creates a new Google Places session token if API is available
 * @returns {google.maps.places.AutocompleteSessionToken|null} Session token or null
 */
function createSessionToken() {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        return new google.maps.places.AutocompleteSessionToken();
    }
    return null;
}

/**
 * Gets place details from Google Places API
 * @param {google.maps.places.PlacesService} placesService - Places service instance
 * @param {string} placeId - Google Place ID
 * @param {google.maps.places.AutocompleteSessionToken} sessionToken - Session token
 * @param {Function} callback - Callback with place result
 */
function getPlaceDetails(placesService, placeId, sessionToken, callback) {
    placesService.getDetails(
        {
            placeId: placeId,
            fields: ['geometry', 'address_components'],
            sessionToken: sessionToken
        },
        function (place, status) {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
                callback(place);
            }
        }
    );
}

/**
 * Shows prediction dropdown for address autocomplete
 * @param {HTMLElement} input - The input element
 * @param {Array} predictions - Google Places predictions
 * @param {Function} onSelect - Callback when prediction is selected
 */
function showPredictions(input, predictions, onSelect) {
    removeDropdown(input);

    if (!predictions || !predictions.length) return;

    var dropdown = document.createElement('div');
    dropdown.className = 'pac-custom-container pac-container pac-logo hdpi';

    var limitedPredictions = predictions.slice(0, 5);

    limitedPredictions.forEach(function (prediction) {
        var item = document.createElement('div');
        item.className = 'pac-item';
        item.innerHTML = `
            <span class="pac-icon pac-icon-marker"></span>
            <div class="pac-item-content">
                <span class="pac-item-query">${prediction.structured_formatting.main_text}</span>
                <span class="pac-secondary-text">${prediction.structured_formatting.secondary_text || ''}</span>
            </div>
        `;
        item.addEventListener('click', function () {
            input.value = prediction.description;
            removeDropdown(input);
            onSelect(prediction.place_id);
        });
        dropdown.appendChild(item);
    });

    input.parentNode.style.position = 'relative';
    input.parentNode.appendChild(dropdown);
}

/**
* This function starts the engine for the autocomplete suggestions in the delivery address input
* REQUIRES LOAD GOOGLE PLACES SCRIPT BEFORE CALL
* @requires jQuery
* @params $input input to handle
* @returns {void}
**/
function initAutocomplete($input, handlePlaceSelected) {
    var input = $input.get(0);
    var autocompleteService = new google.maps.places.AutocompleteService();
    var placesService = new google.maps.places.PlacesService(document.createElement('div'));
    var sessionToken = createSessionToken();

    var debouncedGetPredictions = debounce(function () {
        var value = input.value.trim();
        if (value.length < GOOGLE_PLACES_AUTOCOMPLETE.MIN_CHARS) {
            removeDropdown(input);
            return;
        }

        autocompleteService.getPlacePredictions(
            {
                input: value,
                componentRestrictions: { country: 'mx' },
                sessionToken: sessionToken
            },
            function (predictions, status) {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    showPredictions(input, predictions, function (placeId) {
                        getPlaceDetails(placesService, placeId, sessionToken, function (place) {
                            sessionToken = createSessionToken();
                            handlePlaceSelected({ getPlace: function () { return place; } });
                        });
                    });
                }
            }
        );
    }, GOOGLE_PLACES_AUTOCOMPLETE.DEBOUNCE_MS);

    input.addEventListener('input', debouncedGetPredictions);

    document.addEventListener('click', function (e) {
        if (!input.contains(e.target)) {
            removeDropdown(input);
        }
    });
}


/**
 * Parse location results
 * @requires jQuery
 * @param {Array} results Google address results
 * @returns {Object | null} address
 */
function parseLocationResults({ results }) {
    if (!results.length) {
        return null;
    }

    const [closestLocation] = results;
    let { addressDetails, addressName, zipCode, stateCode } = addressModel.formatAddressComponents(closestLocation);
    const componentsModel = addressModel.getAddressComponents(closestLocation);

    if (addressName.length <= 1 && closestLocation.formatted_address.length) {
        addressName = closestLocation.formatted_address;
    }

    if (addressDetails.length <= 20 && closestLocation.formatted_address.length) {
        addressDetails = closestLocation.formatted_address;
    }

    return {
        addressName,
        addressDetails,
        zipCode,
        stateCode,
        componentsModel
    };
}

/**
 * Sends the current location to find close stores
 * @requires jQuery
 * @param {Number} latitude Device latitude
 * @param {Number} longitude Device longitude
 * @returns {void}
 */
async function getAdddressForLocation (lat, lng) {
    const geocoder = new google.maps.Geocoder();
    const config = {
        location: { lat, lng }
    };

    const results = await geocoder.geocode(config);
    return parseLocationResults(results);
}

/**
 * Fetches latitude and longitude for a given address from Google Places API
 * using jQuery
 * @param {string} address - The address to geocode
 * @returns {void}
 */

async function fetchLatLngFromAddress(addressDetails, errorMsg) {
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
        console.log(errorMsg.unavailable);
        return;
    }

    const geocoder = new google.maps.Geocoder();

    try {
        const results = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: addressDetails }, function (results, status) {
                if (status === google.maps.GeocoderStatus.OK && results[0]) {
                    resolve(results);
                } else {
                    var notFoundMsg = errorMsg ? errorMsg.notfound : '';
                    reject(notFoundMsg + status);
                }
            });
        });

        return results[0].geometry.location;

    } catch (error) {
        console.log(error);
    }
}

function buildAddressDetailsFromForm($form) {
    try {
        const address1 = $form.find('[name$="_address1"]').val() || '';
        const address2 = $form.find('[name$="_address2"]').val() || '';
        const city = $form.find('[name$="_city"]').val() || '';
        const state = $form.find('[name$="_stateCode"]').val() || '';
        const postalCode = $form.find('[name$="_postalCode"]').val() || '';
        const neighborhood = $form.find('[name$="_neighborhood"]').val() || '';
        const country = $form.find('[name$="_country"]').val() || '';
        const externalNumber = $form.find('[name$="_externalNumber"]').val() || '';
        const interiorNumber = $form.find('[name$="_interiorNumber"]').val() || '';

        const addressParts = [address1, address2, externalNumber, interiorNumber, neighborhood, city, state, postalCode, country].filter(part => part.trim() !== '');

        return addressParts.join(', ');
    } catch (error) {
        console.error('Error formatting address from form:', error);

        return '';
    }
}

/**
 * Gets postal code value from form
 * @param {jQuery} $form - Form element
 * @returns {string} Postal code value
 */
function getPostalCodeFromForm($form) {
    return $form.find('[name$="_postalCode"]').val() || '';
}

/**
 * Fetches latitude and longitude for a given address from Google Places API
 * using jQuery
 * @param {string} addressDetails - The address to geocode
 * @param {string} expectedPostalCode - Postal code entered by the shopper
 * @returns {void}
 */

async function fetchPlaceFromAddress(addressDetails, expectedPostalCode) {
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
        console.log('Geocoding service unavailable');
        return;
    }

    const geocoder = new google.maps.Geocoder();

    try {
        const results = await new Promise((resolve, reject) => {
            geocoder.geocode({ address: addressDetails }, function (results, status) {
                if (status === google.maps.GeocoderStatus.OK && results[0]) {
                    resolve(results);
                } else {
                    reject(status);
                }
            });
        });

        const place = results[0];
        if (expectedPostalCode) {
            const googlePostalCode = place.address_components && place.address_components.find(c => c.types.includes('postal_code'));
            if (!googlePostalCode || String(expectedPostalCode).trim() !== googlePostalCode.long_name) {
                return { postalCodeMismatch: true };
            }
        }

        return place;

    } catch (error) {
        console.log(error);
    }
}

module.exports = {
    getAdddressForLocation,
    fetchLatLngFromAddress,
    initAutocomplete,
    buildAddressDetailsFromForm,
    fetchPlaceFromAddress,
    getPostalCodeFromForm
};
